
import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "/Users/michaeltucker/Documents/coding-projects/client/src/firebase.js";

import {
  Button,
  Label,
  FormGroup,
  Form,
  Input,
  FormFeedback,
} from "reactstrap";
import AuthLoginMobileLogo from "components/AuthLoginMobileLogo";
import GoogleSignInButton from "components/GoogleSignInButton";
import {
  buildAttributionSummary,
  captureAdAttribution,
  getSignupAttribution,
  inferLeadSourceFromAttribution,
  trackRegistrationSuccess,
} from "utils/metaPixel";
import ReferralSignupBanner from "components/ReferralSignupBanner";
import { captureReferralFromSearch } from "utils/referralStorage";
import { attachStoredReferralAfterSignup } from "utils/referralSignup";
import {
  EXISTING_PASSWORD_ACCOUNT_MESSAGE,
  AUTH_PROVIDER_IDS,
  getDuplicateAccountEmail,
  getGoogleAuthErrorMessage,
  getGoogleRedirectResult,
  hasUnlinkedPasswordAccountForEmail,
  isDuplicateAccountError,
  logDuplicateAccountAttempt,
  signInWithGooglePopupOrRedirect,
} from "utils/authProviderMethods";
import { trackAuthAnalyticsEvent } from "utils/authTelemetry";
import { FULLSTORY_ELEMENTS } from "utils/fullstoryElements";
import { bootstrapUserShellAfterSignup, ensureUserDocumentForAuthUser, ensureUserDocumentWithRetries } from "utils/userProvisioning";
import { reportAppError } from "utils/reportAppError";
import { captureSignupCountryAfterRegistration } from "utils/captureSignupCountry";
import { buildSignupCountrySignals } from "utils/countryAttribution";
import { getMobileSignupRedirectPath } from "utils/mobileSignupHandoff";

const LEAD_SOURCE_OPTIONS = [
  "Colleague/Friend",
  "Convention Booth",
  "Email",
  "Facebook Ad",
  "Facebook Group",
  "Flyer",
  "Google Ad",
  "Google Search",
  "Instagram Ad",
  "Pinterest Ad",
  "Podcast",
  "Reddit",
  "TikTok Ad",
  "Other",
];

function Register() {
  useEffect(() => {
    const previousHtmlOverflowY = document.documentElement.style.overflowY;
    const previousHtmlHeight = document.documentElement.style.height;
    const previousBodyOverflowY = document.body.style.overflowY;
    const previousBodyHeight = document.body.style.height;
    const rootElement = document.getElementById("root");
    const previousRootHeight = rootElement?.style?.height;

    document.body.classList.add("register-page");
    document.documentElement.style.overflowY = "auto";
    document.documentElement.style.height = "auto";
    document.body.style.overflowY = "auto";
    document.body.style.height = "auto";
    if (rootElement) {
      rootElement.style.height = "auto";
    }

    return () => {
      document.body.classList.remove("register-page");
      document.documentElement.style.overflowY = previousHtmlOverflowY;
      document.documentElement.style.height = previousHtmlHeight;
      document.body.style.overflowY = previousBodyOverflowY;
      document.body.style.height = previousBodyHeight;
      if (rootElement) {
        rootElement.style.height = previousRootHeight || "";
      }
    };
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [leadSource, setLeadSource] = useState("");
  const [showValidation, setShowValidation] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState("");
  const [isGoogleAuthLoading, setIsGoogleAuthLoading] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();

  const buildSignupProvisioningProfile = (authUser = null) => {
    const signupAttribution = getSignupAttribution();
    const countrySignals = buildSignupCountrySignals();
    return {
      firstName,
      lastName,
      email: email || authUser?.email,
      leadSource,
      attribution: signupAttribution || null,
      attributionSummary: buildAttributionSummary(signupAttribution),
      countryAttribution: {
        browserLocale: countrySignals.browserLocale || null,
        browserTimezone: countrySignals.browserTimezone || null,
      },
    };
  };

  const finishSignup = async (authUser, options = {}) => {
    const { created, profile } = await ensureUserDocumentForAuthUser(
      authUser,
      buildSignupProvisioningProfile(authUser)
    );

    if (created) {
      await attachStoredReferralAfterSignup();
      await captureSignupCountryAfterRegistration();
      await trackRegistrationSuccess({
        uid: authUser.uid,
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        triggerSource:
          options.providerId === AUTH_PROVIDER_IDS.GOOGLE
            ? "register_google"
            : "register_email_password",
        registrationMethod:
          options.providerId === AUTH_PROVIDER_IDS.GOOGLE ? "google" : "email_password",
      });

      if (options.providerId === AUTH_PROVIDER_IDS.GOOGLE) {
        trackAuthAnalyticsEvent("signup_google", {
          providerId: AUTH_PROVIDER_IDS.GOOGLE,
          source: "register",
        });
      }

      await bootstrapUserShellAfterSignup(dispatch, authUser.uid);
    } else if (options.providerId === AUTH_PROVIDER_IDS.GOOGLE) {
      trackAuthAnalyticsEvent("login_google", {
        providerId: AUTH_PROVIDER_IDS.GOOGLE,
        source: "register",
      });
    }

    history.push(created ? getMobileSignupRedirectPath() : "/");
  };

  const handleGoogleLogin = async () => {
    setAuthError("");
    setIsGoogleAuthLoading(true);

    try {
      if (email && await hasUnlinkedPasswordAccountForEmail(email)) {
        await logDuplicateAccountAttempt({
          email,
          source: "register",
          reason: "password_method_preflight",
        });
        setAuthError(EXISTING_PASSWORD_ACCOUNT_MESSAGE);
        return;
      }

      const { result, redirected } = await signInWithGooglePopupOrRedirect("signup");
      if (redirected) {
        return;
      }

      await finishSignup(result.user, { providerId: AUTH_PROVIDER_IDS.GOOGLE });
    } catch (error) {
      if (isDuplicateAccountError(error)) {
        await logDuplicateAccountAttempt({
          email: getDuplicateAccountEmail(error) || email,
          source: "register",
          reason: error.code,
        });
      }
      setAuthError(getGoogleAuthErrorMessage(error));
    } finally {
      setIsGoogleAuthLoading(false);
    }
  };

  const register = async (e) => {
    e.preventDefault();
    if (!isValidated()) {
      setShowValidation(true);
      return;
    }

    try {
      const authUser = await createUserWithEmailAndPassword(auth, email, password);
      try {
        await finishSignup(authUser.user);
      } catch (provisionError) {
        reportAppError(provisionError, {
          feature: "user-provisioning",
          action: "register-finish-signup",
          uid: authUser.user?.uid,
          dedupeKey: `register-finish-signup:${authUser.user?.uid || "unknown"}`,
        });

        // Auth already exists — retry provisioning instead of leaving an orphan.
        try {
          await ensureUserDocumentWithRetries(
            authUser.user,
            buildSignupProvisioningProfile(authUser.user)
          );
          await bootstrapUserShellAfterSignup(dispatch, authUser.user.uid);
          history.push("/");
          return;
        } catch (retryError) {
          reportAppError(retryError, {
            feature: "user-provisioning",
            action: "register-retry-provision",
            uid: authUser.user?.uid,
            dedupeKey: `register-retry-provision:${authUser.user?.uid || "unknown"}`,
          });
          setAuthError(
            "Your login was created, but account setup did not finish. Please sign in to complete setup."
          );
        }
      }
    } catch (error) {
      console.error("Registration failed:", error.code, error.message);
      setAuthError(`Registration Failed: ${error.message}`);
    }
  };

  useEffect(() => {
    captureAdAttribution(window.location.search);
    captureReferralFromSearch(window.location.search);
    const inferredLeadSource = inferLeadSourceFromAttribution();
    if (inferredLeadSource) {
      setLeadSource(inferredLeadSource);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const finishRedirectSignup = async () => {
      try {
        const { result, intent } = await getGoogleRedirectResult();
        if (!result || intent !== "signup") {
          return;
        }

        await finishSignup(result.user, { providerId: AUTH_PROVIDER_IDS.GOOGLE });
      } catch (error) {
        if (!isMounted) return;
        if (isDuplicateAccountError(error)) {
          await logDuplicateAccountAttempt({
            email: getDuplicateAccountEmail(error) || email,
            source: "register_redirect",
            reason: error.code,
          });
        }
        setAuthError(getGoogleAuthErrorMessage(error));
      }
    };

    finishRedirectSignup();

    return () => {
      isMounted = false;
    };
  }, []);

  function isEmailValid(value) {
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailPattern.test(value);
  }

  const isValidated = () => {
    if (firstName && lastName && email && password && confirmPassword) {
      if (
        isEmailValid(email) &&
        password.length >= 8 &&
        password === confirmPassword &&
        firstName.length > 0 &&
        firstName.length <= 30 &&
        lastName.length > 0 &&
        lastName.length <= 30
      ) {
        return true;
      }
    }
    return false;
  };

  const firstNameInvalid =
    showValidation && (!firstName || firstName.length === 0 || firstName.length > 30);
  const lastNameInvalid =
    showValidation && (!lastName || lastName.length === 0 || lastName.length > 30);
  const emailInvalid = showValidation && (!email || !isEmailValid(email));
  const passwordInvalid =
    showValidation && (!password || password.length < 8);
  const confirmPasswordInvalid =
    showValidation &&
    (!confirmPassword || confirmPassword !== password);

  return (
    <Form onSubmit={register} className="auth-login-form" noValidate>
      <div className="auth-login-card">
        <AuthLoginMobileLogo />
        <h1 className="auth-login-title">Let&apos;s get started!</h1>
        <p className="auth-login-subtitle auth-login-trial-note">
          Start your{" "}
          <span className="auth-login-trial-highlight">30-day free</span> trial
          now
        </p>

        <ReferralSignupBanner />

        {authError && (
          <div className="auth-login-error-note" role="alert">
            {authError}
          </div>
        )}

        <div className="auth-login-name-row">
          <FormGroup className="auth-login-field">
            <Label for="register-first-name">First name</Label>
            <Input
              id="register-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              type="text"
              autoComplete="given-name"
              className="auth-login-input"
              invalid={firstNameInvalid}
            />
            {firstNameInvalid && (
              <FormFeedback>
                {!firstName
                  ? "Please enter a first name."
                  : "First name must be 30 characters or fewer."}
              </FormFeedback>
            )}
          </FormGroup>

          <FormGroup className="auth-login-field">
            <Label for="register-last-name">Last name</Label>
            <Input
              id="register-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
              type="text"
              autoComplete="family-name"
              className="auth-login-input"
              invalid={lastNameInvalid}
            />
            {lastNameInvalid && (
              <FormFeedback>
                {!lastName
                  ? "Please enter a last name."
                  : "Last name must be 30 characters or fewer."}
              </FormFeedback>
            )}
          </FormGroup>
        </div>

        <FormGroup className="auth-login-field">
          <Label for="register-email">Email</Label>
          <Input
            id="register-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@school.edu"
            type="email"
            autoComplete="email"
            className="auth-login-input"
            invalid={emailInvalid}
          />
          {emailInvalid && (
            <FormFeedback>Please enter a valid email address.</FormFeedback>
          )}
        </FormGroup>

        <FormGroup className="auth-login-field">
          <Label for="register-password">Password</Label>
          <div className="auth-login-password-wrap">
            <Input
              id="register-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              className="auth-login-input auth-login-input--with-toggle fs-mask"
              invalid={passwordInvalid}
            />
            <button
              type="button"
              className="auth-login-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <i className={`fa ${showPassword ? "fa-eye" : "fa-eye-slash"}`} />
            </button>
          </div>
          {passwordInvalid && (
            <FormFeedback>Password must be at least 8 characters.</FormFeedback>
          )}
        </FormGroup>

        <FormGroup className="auth-login-field">
          <Label for="register-confirm-password">Confirm password</Label>
          <div className="auth-login-password-wrap">
            <Input
              id="register-confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              className="auth-login-input auth-login-input--with-toggle fs-mask"
              invalid={confirmPasswordInvalid}
            />
            <button
              type="button"
              className="auth-login-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide passwords" : "Show passwords"}
            >
              <i className={`fa ${showPassword ? "fa-eye" : "fa-eye-slash"}`} />
            </button>
          </div>
          {confirmPasswordInvalid && (
            <FormFeedback>
              {!confirmPassword
                ? "Please confirm your password."
                : "Passwords do not match."}
            </FormFeedback>
          )}
        </FormGroup>

        <FormGroup className="auth-login-field">
          <Label for="register-lead-source">
            Where did you hear about Lesson Launchpad™?
          </Label>
          <Input
            id="register-lead-source"
            value={leadSource}
            onChange={(e) => setLeadSource(e.target.value)}
            type="select"
            className="auth-login-input auth-login-select"
          >
            <option value="">Select one (optional)</option>
            {LEAD_SOURCE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Input>
        </FormGroup>

        <Button
          type="submit"
          className="btn design-system-primary-solid auth-login-submit"
          data-fs-element={FULLSTORY_ELEMENTS.authentication.createAccount}
        >
          Get started
        </Button>

        <div className="auth-login-divider">
          <span>or</span>
        </div>

        <GoogleSignInButton
          onClick={handleGoogleLogin}
          loading={isGoogleAuthLoading}
          data-fs-element={FULLSTORY_ELEMENTS.authentication.googleCreateAccount}
        />

        <p className="auth-login-footer-text">
          Already have an account?{" "}
          <Link
            to="/auth/login"
            className="auth-login-footer-link"
            data-fs-element={FULLSTORY_ELEMENTS.authentication.switchToLogin}
          >
            Sign in
          </Link>
        </p>
      </div>
    </Form>
  );
}

export default Register;
